/**
 * BUSINESS LAYER (BL): CARRERA CONTINUA
 * Capa de lógica de negocio y envoltorios de comunicación para Carrera Continua.
 */

import {
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    UploadAssetDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    CreateLiveMatchDL,
    StartLiveMatchDL,
    PauseLiveMatchDL,
    ResumeLiveMatchDL,
    FinishLiveMatchEarlyDL,
    ResetLiveMatchDL,
    GetLiveMatchStateDL,
    AddLiveTapsDL,
    ShowCarreraContinuaCoverDL,
    ShowCarreraContinuaGameDL
} from '../DL/CarreraContinua';

import { SetScreenV2MainDynamic } from './ScreenV2';

/**
 * Obtiene el catálogo de temas de Carrera Continua para una sala u operador.
 */
export const GetThemesBL = async (roomId = null, operatorId = null) => {
    try {
        const res = await GetThemesDL(roomId, operatorId);
        if (!res.success) {
            return { success: false, message: res.message || 'No fue posible cargar los temas.', data: [] };
        }
        return { success: true, data: Array.isArray(res.data) ? res.data : [] };
    } catch (error) {
        return { success: false, message: 'Error inesperado al obtener los temas.', data: [] };
    }
};

/**
 * Obtiene el detalle de un tema por ID.
 */
export const GetThemeByIdBL = async (id) => {
    if (!id) return { success: false, message: 'El ID del tema es requerido.' };
    return await GetThemeByIdDL(id);
};

/**
 * Crea un nuevo tema personalizado de Carrera Continua.
 */
export const CreateThemeBL = async (themeData) => {
    if (!themeData || !themeData.name || !themeData.name.trim()) {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }
    return await CreateThemeDL(themeData);
};

/**
 * Actualiza un tema existente.
 */
export const UpdateThemeBL = async (id, themeData) => {
    if (!id) return { success: false, message: 'El ID del tema es requerido.' };
    if (!themeData || !themeData.name || !themeData.name.trim()) {
        return { success: false, message: 'El nombre del tema es obligatorio.' };
    }
    return await UpdateThemeDL(id, themeData);
};

/**
 * Elimina un tema personalizado.
 */
export const DeleteThemeBL = async (id) => {
    if (!id) return { success: false, message: 'El ID del tema es requerido.' };
    return await DeleteThemeDL(id);
};

/**
 * Clona un tema existente como una copia editable.
 */
export const CloneThemeBL = async (id, newName) => {
    if (!id) return { success: false, message: 'El ID del tema es requerido.' };
    return await CloneThemeDL(id, newName);
};

/**
 * Sube y optimiza un recurso gráfico para Carrera Continua.
 */
export const UploadAssetBL = async (formData) => {
    if (!formData) return { success: false, message: 'No se especificó el archivo a subir.' };
    return await UploadAssetDL(formData);
};

/**
 * Obtiene el historial de partidas disputadas en una sala.
 */
export const GetMatchHistoryBL = async (roomId, limit = 20) => {
    if (!roomId) {
        return { success: false, message: 'El identificador de sala es obligatorio.', data: [] };
    }
    try {
        const res = await GetMatchHistoryDL(roomId, limit);
        if (!res.success) {
            return { success: false, message: res.message || 'No se pudo obtener el historial.', data: [] };
        }
        return { success: true, data: Array.isArray(res.data) ? res.data : [] };
    } catch (error) {
        return { success: false, message: 'Error de red al consultar el historial.', data: [] };
    }
};

/**
 * Guarda el resultado final de una partida.
 */
export const SaveMatchResultBL = async (matchData) => {
    if (!matchData || !matchData.roomId) {
        return { success: false, message: 'Datos incompletos para guardar el resultado.' };
    }
    return await SaveMatchResultDL(matchData);
};

/**
 * Inicializa una nueva partida en vivo en estado 'Waiting'.
 */
export const CreateLiveMatchBL = async ({ roomId, themeId, durationSeconds = 45, totalKilometers = 5 }) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    if (!themeId) return { success: false, message: 'Debes seleccionar un tema válido antes de crear la partida.' };

    return await CreateLiveMatchDL({
        roomId,
        themeId: Number(themeId),
        durationSeconds: Number(durationSeconds) || 45,
        totalKilometers: Number(totalKilometers) || 5
    });
};

/**
 * Inicia la cuenta regresiva sincronizada (3, 2, 1) y posterior arranque de carrera.
 */
export const StartLiveMatchBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await StartLiveMatchDL({ roomId });
};

/**
 * Pausa la carrera activa.
 */
export const PauseLiveMatchBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await PauseLiveMatchDL({ roomId });
};

/**
 * Reanuda la carrera pausada.
 */
export const ResumeLiveMatchBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await ResumeLiveMatchDL({ roomId });
};

/**
 * Finaliza forzadamente una carrera calculando resultados finales.
 */
export const FinishLiveMatchEarlyBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await FinishLiveMatchEarlyDL({ roomId });
};

/**
 * Reinicia la carrera de una sala devolviéndola al estado 'Waiting'.
 */
export const ResetLiveMatchBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await ResetLiveMatchDL({ roomId });
};

/**
 * Obtiene el estado actual en memoria de la partida.
 */
export const GetLiveMatchStateBL = async (roomId) => {
    if (!roomId) return { success: false, message: 'El roomId es obligatorio.' };
    return await GetLiveMatchStateDL(roomId);
};

/**
 * Proyecta la portada de Carrera Continua en la pantalla gigante (/screen y /screenv2).
 */
export const ShowCarreraContinuaCoverBL = async (roomId = null, themeId = null, screenId = 1) => {
    try {
        const res = await ShowCarreraContinuaCoverDL(roomId, themeId);
        try {
            await SetScreenV2MainDynamic(screenId, 'CarreraContinuaCover');
        } catch (e) {
            // Silencioso si ScreenV2 no está activo
        }
        return res.success ? { success: true, message: 'Portada de Carrera Continua proyectada.' } : res;
    } catch (error) {
        return { success: false, message: 'Error al proyectar la portada.' };
    }
};

/**
 * Proyecta la pantalla de juego (pista de atletismo) de Carrera Continua en la pantalla gigante (/screen y /screenv2).
 */
export const ShowCarreraContinuaGameBL = async (roomId = null, themeId = null, screenId = 1) => {
    try {
        const res = await ShowCarreraContinuaGameDL(roomId, themeId);
        try {
            await SetScreenV2MainDynamic(screenId, 'CarreraContinua');
        } catch (e) {
            // Silencioso si ScreenV2 no está activo
        }
        return res.success ? { success: true, message: 'Pantalla de juego proyectada.' } : res;
    } catch (error) {
        return { success: false, message: 'Error al proyectar el juego.' };
    }
};

/**
 * Registra y envía un lote acumulado de taps hacia el backend.
 */
export const AddLiveTapsBL = async ({ roomId, zoneIdentifier, tapsCount }) => {
    if (!roomId || !zoneIdentifier || !tapsCount || tapsCount <= 0) {
        return { success: false, message: 'Parámetros insuficientes para registrar taps.' };
    }
    return await AddLiveTapsDL({ roomId, zoneIdentifier, tapsCount });
};
